import React, { useEffect, useState } from 'react';
import { Button } from '@trackman/web-shared-components';
import { ISession, ILeague, ILevel, IField, IVenue, IApiSessionTeam } from 'interfaces/session/ISession';
import { useInjection } from 'services/ioc/ioc.react';
import { ServiceTypes } from 'constants/ServiceTypes';
import { IOwningOrganization } from 'interfaces/session/IOwningOrganization';
import { ISearchTeam } from 'interfaces/team/ISearchTeam';
import { observer } from 'mobx-react-lite';
import { MergeModalFooter } from 'components/modals/MergeModal/styles/MergeModalStyles';
import { CenteredLoader } from '@trackman/baseball-shared-components';
import { ISessionUpdateRequest } from '../../../../../interfaces/session/ISessionUpdateRequest';
import {
  Content,
  Controls,
  Divider,
  InfoNote,
  LoaderWrapper,
  RefreshTime,
  RefreshWrapper,
  StyledButton,
  StyledInfoIcon,
  StyledModal,
  StyledRotateIcon,
  TabsWrapper,
} from './styles/EditSessionModalStyles';
import { IUmpire } from 'interfaces/umpire/IUmpire';
import TeamsAndSettings from './components/TeamsAndSettings/TeamsAndSettings';
import Features from './components/Features/Features';
import { SessionPlayStore } from 'stores/SessionPlayStore';
import dayjs, { Dayjs } from 'dayjs';
import { SessionVerificationStore } from 'stores/SessionVerificationStore';
import Tabs from './components/Tabs/Tabs';
import { ImportCSV } from '../../ImportCSV/ImportCSV';

interface IProps {
  visible: boolean;
  session?: ISession;
  onDismissCallback: () => void;
  onApplyCallback: (update: ISessionUpdateRequest) => void;
  onCsvSelected: (file: any) => void;
  contextualDataLoading?: boolean;
  selectedUmpire?: IUmpire;
  setSelectedUmpire: (umpire?: IUmpire) => void;
}

const EditSessionModal: React.FC<IProps> = observer(
  ({ visible, session, onDismissCallback, onApplyCallback, contextualDataLoading, selectedUmpire, setSelectedUmpire, onCsvSelected }) => {
    const sessionStore = useInjection<SessionVerificationStore>(ServiceTypes.SessionVerificationStore);
    const [selectedLeague, setSelectedLeague] = useState<ILeague>({} as ILeague);
    const [selectedLevel, setSelectedLevel] = useState<ILevel>({} as ILevel);
    const [selectedField, setSelectedField] = useState<IField>({} as IField);
    const [selectedVenue, setSelectedVenue] = useState<IVenue>({} as IVenue);
    const [dailySequenceNumber, setDailySequenceNumber] = useState<number | undefined>(undefined);
    const [externalSessionId, setExternalSessionId] = useState<string>();
    const [externalEventId, setExternalEventId] = useState<string>();
    const [selectedOrg, setSelectedOrg] = useState<IOwningOrganization>();
    const [owningOrganizations, setOwningOrganizations] = useState<IOwningOrganization[]>([]);
    const [selectedHomeTeam, setSelectedHomeTeam] = useState<ISearchTeam>();
    const [selectedAwayTeam, setSelectedAwayTeam] = useState<ISearchTeam>();
    const [isOrgLoading, setIsOrgLoading] = useState<boolean>(false);
    const [invalidExternalId, setInvalidExternalId] = useState(false);
    const [activeTab, setActiveTab] = useState<number>(0);
    const [areRostersLoading, setAreRostersLoading] = useState<boolean>(false);
    const sessionPlayStore: SessionPlayStore = useInjection<SessionPlayStore>(ServiceTypes.SessionPlayStore);
    const emptyOrganization = { id: '', name: '---', shortName: '' };
    const [isAnyChange, setIsAnyChange] = useState<boolean>(false);
    const [lastRefresh, setLastRefresh] = useState<Dayjs | null>(null);

    const fetchRosters = async () => {
      if (areRostersLoading || !session) {
        return;
      }
      try {
        setAreRostersLoading(true);
        await sessionPlayStore.loadRosters(session.homeTeam.id, session.awayTeam.id);
        setLastRefresh(dayjs());
      } finally {
        setAreRostersLoading(false);
      }
    };

    const getHomeTeam = () => {
      if (session && session.homeTeam?.id === selectedHomeTeam!.id) {
        return session.homeTeam;
      }
      return {
        id: selectedHomeTeam!.id,
        name: selectedHomeTeam!.name,
        shortName: selectedHomeTeam!.shortName,
        externalId: undefined,
        lineup: [],
      } as IApiSessionTeam;
    };

    const getAwayTeam = () => {
      if (session && session.awayTeam?.id === selectedAwayTeam!.id) {
        return session.awayTeam;
      }
      return {
        id: selectedAwayTeam!.id,
        name: selectedAwayTeam!.name,
        shortName: selectedAwayTeam!.shortName,
        externalId: undefined,
        lineup: [],
      } as IApiSessionTeam;
    };

    const [isLoading, setIsLoading] = useState(false);

    const useNewSettingsHandler = async () => {
      if (session && externalSessionId && externalSessionId !== session.externalSessionId) {
        setIsLoading(true);
        const isExternalIdValid = await sessionStore.validateExternalSessionId(externalSessionId).finally(() => {
          setIsLoading(false);
        });

        if (!isExternalIdValid) {
          setInvalidExternalId(true);
          return;
        }
      }

      if (!selectedAwayTeam || !selectedHomeTeam || !session) {
        return;
      }

      const updateRequest = {
        id: session.id,
        field: { ...selectedField },
        venue: { ...selectedVenue },
        owningOrganizationId: selectedOrg && selectedOrg.id !== '' ? selectedOrg.id : undefined,
        league: { ...selectedLeague },
        level: { ...selectedLevel },
        isVerified: false,
        isPlayerTrackingVerified: false,
        homeTeam: getHomeTeam(),
        awayTeam: getAwayTeam(),
        externalSessionId,
        dailySequenceNumber,
        umpire: {
          ...selectedUmpire,
          isPlaceholder: selectedUmpire?.isPlaceholder ? true : false,
        },
      } as ISessionUpdateRequest;

      onApplyCallback(updateRequest);
    };

    const fetchOwningOrganizations = async () => {
      if (!selectedField.id) {
        setOwningOrganizations([emptyOrganization]);
        return;
      }

      try {
        setIsOrgLoading(true);
        const owningOrgs = await sessionStore.fetchOwningOrganizations(selectedField.id);
        setOwningOrganizations([emptyOrganization, ...owningOrgs]);
      } finally {
        setIsOrgLoading(false);
      }
    };

    useEffect(() => {
      if (session) {
        setSelectedLeague(session.league);
        setExternalSessionId(session.externalSessionId);
        setExternalEventId(session.externalEventId);
        setSelectedLevel(session.level);
        setSelectedField(sessionStore.fields.find((x) => x.id === session.field.id) || ({} as IField));
        setSelectedVenue(sessionStore.venues.find((x) => x.id === session.venue.id) || ({} as IVenue));
        setSelectedHomeTeam(session.homeTeam && { ...session.homeTeam });
        setSelectedAwayTeam(session.awayTeam && { ...session.awayTeam });
        setDailySequenceNumber(session.dailySequenceNumber || undefined);
        setSelectedUmpire(sessionStore.umpires.find((umpire) => umpire.id === session.umpire?.id));
      }
    }, [session, sessionStore.fields, sessionStore.venues]);

    useEffect(() => {
      fetchOwningOrganizations();
      setSelectedOrg(emptyOrganization);
    }, [selectedField]);

    useEffect(() => {
      if (session) {
        if (
          selectedLeague?.id !== session.league?.id ||
          externalSessionId !== session.externalSessionId ||
          selectedLevel?.id !== session.level?.id ||
          selectedField?.id !== session.field?.id ||
          selectedVenue?.id !== session?.venue?.id ||
          selectedHomeTeam?.id !== session.homeTeam?.id ||
          selectedAwayTeam?.id !== session.awayTeam?.id ||
          dailySequenceNumber !== session.dailySequenceNumber ||
          selectedUmpire?.id !== session.umpire?.id
        ) {
          setIsAnyChange(true);
        } else {
          setIsAnyChange(false);
        }
      }
    }, [
      selectedLeague,
      externalSessionId,
      selectedLevel,
      selectedField,
      selectedVenue,
      selectedHomeTeam,
      selectedAwayTeam,
      dailySequenceNumber,
      selectedUmpire,
    ]);

    return (
      <>
        {!session || !visible ? null : (
          <StyledModal open={visible} onClose={onDismissCallback} maxWidth='lg' title='Session settings'>
            {(isLoading || contextualDataLoading) && (
              <LoaderWrapper>
                <CenteredLoader size='large' />
              </LoaderWrapper>
            )}
            <Content>
              <TabsWrapper>
                <Tabs activeTab={activeTab} setActiveTab={setActiveTab} />
                {activeTab === 0 && (
                  <RefreshWrapper>
                    {lastRefresh && (
                      <div>
                        <div>Last refreshed:</div>
                        <RefreshTime>{`${lastRefresh.format('DD/MM/YYYY')} @ ${lastRefresh.format('hh:mm')}`}</RefreshTime>
                      </div>
                    )}
                    <StyledButton
                      outlook='solid'
                      intent='primary'
                      label='Refresh rosters'
                      icon={() => <StyledRotateIcon />}
                      isDisabled={areRostersLoading}
                      onClick={fetchRosters}
                      width='156px'
                    />
                    <ImportCSV onFileUpload={onCsvSelected} disabled={!session || !sessionStore.verifySessionProperties(session)} />
                  </RefreshWrapper>
                )}
              </TabsWrapper>
              {activeTab === 0 && (
                <TeamsAndSettings
                  selectedField={selectedField}
                  setSelectedField={setSelectedField}
                  selectedLeague={selectedLeague}
                  setSelectedLeague={setSelectedLeague}
                  selectedLevel={selectedLevel}
                  setSelectedLevel={setSelectedLevel}
                  selectedVenue={selectedVenue}
                  setSelectedVenue={setSelectedVenue}
                  externalSessionId={externalSessionId}
                  invalidExternalId={invalidExternalId}
                  owningOrganizations={owningOrganizations}
                  selectedOrg={selectedOrg}
                  setDailySequenceNumber={setDailySequenceNumber}
                  setExternalSessionId={setExternalSessionId}
                  setInvalidExternalId={setInvalidExternalId}
                  setSelectedAwayTeam={setSelectedAwayTeam}
                  setSelectedHomeTeam={setSelectedHomeTeam}
                  setSelectedOrg={setSelectedOrg}
                  setSelectedUmpire={setSelectedUmpire}
                  visible={visible}
                  dailySequenceNumber={dailySequenceNumber}
                  selectedAwayTeam={selectedAwayTeam}
                  selectedHomeTeam={selectedHomeTeam}
                  selectedUmpire={selectedUmpire}
                  session={session}
                  externalEventId={externalEventId}
                />
              )}
              {activeTab === 1 && <Features session={session} />}

              <Divider />
              <MergeModalFooter>
                {isAnyChange && (
                  <InfoNote>
                    <StyledInfoIcon />
                    Changes are only finally saved when pressing “submit” on the main page.
                  </InfoNote>
                )}
                <Controls>
                  <Button intent='tertiary' label='Cancel' onClick={onDismissCallback} />
                  <Button
                    intent='primary'
                    label='Save'
                    disabled={!selectedAwayTeam || !selectedHomeTeam || !selectedLeague || !selectedLevel}
                    onClick={useNewSettingsHandler}
                  />
                </Controls>
              </MergeModalFooter>
            </Content>
          </StyledModal>
        )}
      </>
    );
  }
);

export default EditSessionModal;
